import { canTriggerRunPublishedWorkflow } from '@/services/monitoringApi';
import type { NodeExecution, Run, RunStatus } from '@/types/monitoring';

export const RUN_RETRY_TOOLTIP =
  'Reruns with the original trigger inputs using the current workflow definition.';
export const ACTION_RESUBMIT_TOOLTIP =
  "Resubmits this run from this action using the run's original workflow version.";
export const ACTION_RESUBMIT_UNFINISHED_REASON =
  'Available after the run finishes.';

type TriggerMap = Record<string, { type?: unknown } | undefined>;

const asTriggerMap = (value: unknown): TriggerMap | undefined =>
  value && typeof value === 'object' ? (value as TriggerMap) : undefined;

export type RunTriggerNameSources = {
  /** Trigger name recorded on the run itself (historical trigger identity). */
  historicalTriggerName?: string | null | undefined;
  /** Triggers from the run's workflow snapshot. */
  snapshotTriggers?: unknown;
  /** Triggers from the CURRENT workflow definition. */
  currentTriggers?: unknown;
};

/**
 * Trigger name for `triggers/{trigger}/histories/{run}/resubmit`. The runtime
 * locates the trigger history by the PROVIDED name (and falls back to the sole
 * current trigger after a rename while keeping that name), so the run's own
 * historical trigger name wins; only a single snapshot/current key can be a
 * fallback. A newer definition cannot disambiguate a multi-trigger snapshot.
 */
export const resolveRetryTriggerName = ({
  historicalTriggerName,
  snapshotTriggers,
  currentTriggers,
}: RunTriggerNameSources): string | null => {
  if (historicalTriggerName) return historicalTriggerName;
  for (const triggers of [snapshotTriggers, currentTriggers]) {
    const keys = Object.keys(asTriggerMap(triggers) ?? {});
    if (keys.length > 0) return keys.length === 1 ? keys[0]! : null;
  }
  return null;
};

const typeOf = (value: { type?: unknown } | undefined): string | undefined =>
  typeof value?.type === 'string' ? value.type : undefined;

/** Type of the trigger the runtime will execute: current by name, else the sole current trigger (rename fallback), else the snapshot. */
const resolveTriggerType = (
  triggerName: string,
  currentTriggers: unknown,
  snapshotTriggers: unknown,
): string | undefined => {
  const current = asTriggerMap(currentTriggers);
  const byName = typeOf(current?.[triggerName]);
  if (byName) return byName;
  if (current) {
    const values = Object.values(current);
    if (values.length === 1) {
      const sole = typeOf(values[0]);
      if (sole) return sole;
    }
  }
  return typeOf(asTriggerMap(snapshotTriggers)?.[triggerName]);
};

export type RunRetryEligibility =
  | { eligible: true; triggerName: string }
  | { eligible: false; reason: string };

export type RunRetryContext = {
  hasEditPermission: boolean;
  currentTriggers?: unknown;
  /** Used only when the run has no `triggerName` (e.g. the run's trigger node name). */
  fallbackTriggerName?: string | null | undefined;
};

type RetryableRun = Pick<Run, 'status' | 'isDraftRun' | 'workflowSnapshot' | 'triggerName'>;

const getSnapshotTriggers = (run: RetryableRun): unknown =>
  (run.workflowSnapshot?.definition as { triggers?: unknown } | undefined)?.triggers;

/** Whole-run retry guards shared by the run header and the bulk toolbar. */
export const getRunRetryEligibility = (
  run: RetryableRun,
  { hasEditPermission, currentTriggers, fallbackTriggerName }: RunRetryContext,
): RunRetryEligibility => {
  if (!hasEditPermission) return { eligible: false, reason: 'You need edit permission to retry runs.' };
  if (run.isDraftRun) return { eligible: false, reason: 'Draft runs cannot be retried.' };
  if (run.status === 'running') return { eligible: false, reason: 'Run is still in progress.' };

  const snapshotTriggers = getSnapshotTriggers(run);
  const triggerName = resolveRetryTriggerName({
    historicalTriggerName: run.triggerName || fallbackTriggerName,
    snapshotTriggers,
    currentTriggers,
  });
  if (!triggerName) return { eligible: false, reason: 'The run trigger could not be determined.' };

  const triggerType = resolveTriggerType(triggerName, currentTriggers, snapshotTriggers);
  if (!canTriggerRunPublishedWorkflow(triggerType)) {
    return { eligible: false, reason: 'This trigger type does not support retry.' };
  }
  return { eligible: true, triggerName };
};

export type ActionResubmitState =
  | { visible: false }
  | { visible: true; disabled: boolean; reason: string };

export type ActionResubmitContext = {
  nodeExecution: Pick<NodeExecution, 'nodeId' | 'canResubmit'> | null | undefined;
  runStatus: RunStatus | null | undefined;
  isDraftRun: boolean;
  hasEditPermission: boolean;
  /** A child item (tool action, chat message, loop repetition, agent iteration) is selected. */
  isChildSelectionActive: boolean;
};

/**
 * "Submit from this action" trusts only an explicit runtime `canResubmit: true`
 * on the selected action itself; nothing is inferred for repetitions,
 * tool actions, chat messages, or triggers.
 */
export const getActionResubmitState = ({
  nodeExecution,
  runStatus,
  isDraftRun,
  hasEditPermission,
  isChildSelectionActive,
}: ActionResubmitContext): ActionResubmitState => {
  if (!nodeExecution || nodeExecution.canResubmit !== true) return { visible: false };
  if (nodeExecution.nodeId.startsWith('trigger-')) return { visible: false };
  if (isChildSelectionActive || isDraftRun || !hasEditPermission || !runStatus) return { visible: false };
  if (runStatus === 'running') {
    return { visible: true, disabled: true, reason: ACTION_RESUBMIT_UNFINISHED_REASON };
  }
  return { visible: true, disabled: false, reason: ACTION_RESUBMIT_TOOLTIP };
};
